import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import API from '../api';
import tronIcon from '../image/tron.svg';

const WithdrawPage = ({ user, setUser, setPath }) => {
  // States for USDT withdrawal
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawState, setWithdrawState] = useState('idle'); // idle, email_verification, processing, success, failed, timeout
  const [withdrawId, setWithdrawId] = useState(null);
  const [withdrawResultMessage, setWithdrawResultMessage] = useState('');
  const [withdrawEmailCode, setWithdrawEmailCode] = useState('');
  const [isRequestingEmailCode, setIsRequestingEmailCode] = useState(false);
  const [isConfirmingWithdraw, setIsConfirmingWithdraw] = useState(false);
  const [showWithdrawHistory, setShowWithdrawHistory] = useState(false);
  const [withdrawHistory, setWithdrawHistory] = useState(null);
  const [hasCompletedVerification, setHasCompletedVerification] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(true);
  const pendingWithdrawStorageKey = 'pendingWithdrawId';

  const redirectToVerification = useCallback(() => {
    window.history.pushState(null, '', '/verification?returnTo=/withdraw&action=withdraw-usdt');
    setPath('/verification');
  }, [setPath]);

  // Effect to handle withdrawal history view state from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const showHistory = urlParams.get('history') === 'true';
    setShowWithdrawHistory(showHistory);
    if (showHistory) {
      fetchWithdrawHistory();
    }
  }, []);

  // Restore pending withdrawal after reload
  useEffect(() => {
    const pendingId = localStorage.getItem(pendingWithdrawStorageKey);
    if (pendingId) {
      setWithdrawId(pendingId);
      setWithdrawState('processing');
    }
  }, []);

  useEffect(() => {
    API.get('/verification/')
      .then((res) => {
        if (res.data?.verified === true) {
          setHasCompletedVerification(true);
          setIsCheckingVerification(false);
        } else {
          redirectToVerification();
        }
      })
      .catch((err) => {
        console.error(err);
        setHasCompletedVerification(false);
        redirectToVerification();
      });
  }, [redirectToVerification]);

  // Function to update URL when showing/hiding history
  const updateHistoryState = (show) => {
    const url = new URL(window.location);
    if (show) {
      url.searchParams.set('history', 'true');
    } else {
      url.searchParams.delete('history');
    }
    window.history.pushState({}, '', url);
    setShowWithdrawHistory(show);
    if (show) {
      fetchWithdrawHistory();
    }
  };

  // Listen for browser back/forward so path state stays in sync
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      setShowWithdrawHistory(urlParams.get('history') === 'true');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const validateWithdrawInputs = () => {
    if (!withdrawAmount || !withdrawAddress) {
      toast.error('Veuillez remplir tous les champs');
      return null;
    }
    const amountVal = parseFloat(withdrawAmount);
    if (isNaN(amountVal) || amountVal < 10) {
      toast.error('Le montant minimum de retrait est de 10 USDT');
      return null;
    }
    return amountVal;
  };

  const ensureUserVerification = async () => {
    if (hasCompletedVerification) {
      return true;
    }
    try {
      const verificationRes = await API.get('/verification/');
      if (verificationRes.data?.verified) {
        setHasCompletedVerification(true);
        return true;
      }
      redirectToVerification();
      return false;
    } catch (err) {
      console.error(err);
      redirectToVerification();
      return false;
    }
  };

  const requestWithdrawEmailCode = async () => {
    const amountVal = validateWithdrawInputs();
    if (!amountVal) return;

    const isVerified = await ensureUserVerification();
    if (!isVerified) return;

    setIsRequestingEmailCode(true);
    try {
      await API.post('/withdraws/request-email-code/', { amount: amountVal, address: withdrawAddress, network: 'TRX' });
      setWithdrawEmailCode('');
      setWithdrawState('email_verification');
      toast.success('Code de vérification envoyé par email.');
    } catch (err) {
      console.error(err);
      if (err.response?.data?.code === 'verification_required') {
        redirectToVerification();
      } else {
        const errMsg = err.response?.data?.error || "Impossible d'envoyer le code de vérification";
        toast.error(errMsg);
      }
    } finally {
      setIsRequestingEmailCode(false);
    }
  };

  // Handler for USDT withdrawal
  const handleWithdraw = async (skipVerificationCheck = false) => {
    const amountVal = validateWithdrawInputs();
    if (!amountVal) return;

    if (!skipVerificationCheck && !hasCompletedVerification) {
      const isVerified = await ensureUserVerification();
      if (!isVerified) return;
    }
    const emailCode = withdrawEmailCode.trim();
    if (!emailCode) {
      toast.error('Veuillez saisir le code reçu par email');
      return;
    }
    setIsConfirmingWithdraw(true);
    setWithdrawState('processing');
    try {
      const res = await API.post('/withdraws/', { amount: amountVal, address: withdrawAddress, network: 'TRX', email_code: emailCode });
      setWithdrawId(res.data.withdraw_id);
      localStorage.setItem(pendingWithdrawStorageKey, res.data.withdraw_id);
      if (res.data.balance_usdt !== undefined) {
        setUser(prevUser => ({ ...prevUser, balance_usdt: res.data.balance_usdt }));
      }
      setWithdrawAmount('');
      setWithdrawAddress('');
      setWithdrawEmailCode('');
      toast.success('Retrait initié. Le statut sera mis à jour automatiquement.');
    } catch (err) {
      console.error(err);
      if (err.response?.data?.code === 'verification_required') {
        redirectToVerification();
      } else {
        const errMsg = err.response?.data?.error || 'Échec de la demande de retrait';
        toast.error(errMsg);
      }
      const codeError = ['withdrawal_email_code_required', 'invalid_withdrawal_email_code', 'expired_withdrawal_email_code'];
      setWithdrawState(codeError.includes(err.response?.data?.code) ? 'email_verification' : 'idle');
    } finally {
      setIsConfirmingWithdraw(false);
    }
  };

  // Effect for polling withdrawal status
  useEffect(() => {
    let intervalId, timeoutId;
    const poll = async () => {
      if (!withdrawId) return;
      try {
        const res = await API.get(`/withdraws/${withdrawId}/`);
        const status = res.data.status;
        if (status === 6) { // Completed
          setWithdrawState('success');
          setWithdrawResultMessage('Retrait effectué avec succès.');
          // Update user balance
          setUser(prevUser => ({ ...prevUser, balance_usdt: res.data.new_balance }));
          setWithdrawId(null); // Stop polling
          localStorage.removeItem(pendingWithdrawStorageKey);
        } else if ([1, 3, 5].includes(status)) { // Canceled, Rejected, Failure
          setWithdrawState('failed');
          setWithdrawResultMessage(res.data.error_message || 'Le retrait a échoué.');
          // Also update on refund
          setUser(prevUser => ({ ...prevUser, balance_usdt: res.data.new_balance }));
          setWithdrawId(null); // Stop polling
          localStorage.removeItem(pendingWithdrawStorageKey);
        }
        // If still PENDING, do nothing, interval will poll again
      } catch (err) {
        console.error('Error polling withdrawal status:', err);
        setWithdrawState('failed');
        setWithdrawResultMessage('Erreur lors de la vérification du statut.');
        setWithdrawId(null);
        localStorage.removeItem(pendingWithdrawStorageKey);
      }
    };

    if (withdrawState === 'processing' && withdrawId) {
      poll();
      intervalId = setInterval(poll, 5000);

      // Set a timeout
      timeoutId = setTimeout(() => {
        if (withdrawState === 'processing') {
           setWithdrawState('timeout');
           setWithdrawResultMessage('Le retrait est toujours en attente. Il pourrait y avoir un problème. Veuillez vérifier ou contacter le support.');
           setWithdrawId(null);
           localStorage.removeItem(pendingWithdrawStorageKey);
        }
      }, 600000); // 10 minutes timeout
    }

    // Cleanup function
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [withdrawState, withdrawId, setUser]);

  // Function to fetch withdrawal history
  const fetchWithdrawHistory = async () => {
    try {
      const res = await API.get('/withdraws/');
      setWithdrawHistory(res.data);
    } catch (err) {
      console.error('Error fetching withdrawal history:', err);
      setWithdrawHistory([]);
    }
  };

  // Function to get status badge class
  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: 'En attente', class: 'pending' },
      1: { label: 'Annulé', class: 'declined' },
      2: { label: 'En approbation', class: 'pending' },
      3: { label: 'Rejeté', class: 'declined' },
      4: { label: 'En traitement', class: 'pending' },
      5: { label: 'Échec', class: 'declined' },
      6: { label: 'Terminé', class: 'approved' }
    };
    return statusMap[status] || { label: 'Inconnu', class: 'pending' };
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isCheckingVerification || !hasCompletedVerification) {
    return null;
  }

  const parsedUsdtBalance = parseFloat(user?.balance_usdt);
  const availableUsdtBalance = Number.isFinite(parsedUsdtBalance)
    ? new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 8 }).format(parsedUsdtBalance)
    : '0';

  const parsedWithdrawAmount = parseFloat(withdrawAmount);
  const estimatedReceived = Number.isFinite(parsedWithdrawAmount) && parsedWithdrawAmount >= 10
    ? Math.max(parsedWithdrawAmount - 1, 0).toFixed(2)
    : null;

  return (
    <>
      <section className={`withdraw-section ${showWithdrawHistory ? 'withdraw-section-expanded' : ''}`}>
      <div className={`withdraw-container ${showWithdrawHistory ? 'withdraw-container-expanded' : ''}`}>
        <div className="withdraw-header withdraw-modern-header">
          <div className="withdraw-title-block">
            <h2>Retrait USDT</h2>
            <p>Envoyez vos USDT vers un portefeuille externe en toute sécurité.</p>
          </div>
          <div className="withdraw-address-flex">
            <div className="network-badge">
              <img src={tronIcon} alt="TRON" className="network-icon" />
              <span className="network-label-full">Réseau TRC20</span>
              <span className="network-label-short">TRC20</span>
            </div>
            <button
              onClick={() => updateHistoryState(true)}
              className="withdraw-history-button-styled"
              title="Historique des retraits"
              aria-label="Afficher l'historique des retraits"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12L14.5 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5.25 12C5.25 8.27208 8.27208 5.25 12 5.25C15.7279 5.25 18.75 8.27208 18.75 12C18.75 15.7279 15.7279 18.75 12 18.75C10.0716 18.75 8.32466 17.9771 7.06199 16.7145" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M7 14L5.13397 16.866L2.26795 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="withdraw-content">
          {!showWithdrawHistory ? (
            <>
              {withdrawState === 'idle' && (
                <form className="withdraw-form" onSubmit={(e) => { e.preventDefault(); requestWithdrawEmailCode(); }}>
                  <div className="withdraw-balance-strip">
                    <div>
                      <span>Solde disponible</span>
                      <strong>{availableUsdtBalance} USDT</strong>
                    </div>
                    <span className="withdraw-balance-symbol" aria-hidden="true">₮</span>
                  </div>

                  <div className="form-group">
                    <label className="withdraw-label">
                      <span className="withdraw-field-heading">
                        <span className="withdraw-field-label">Adresse du portefeuille</span>
                        <span className="withdraw-field-helper">Réseau TRC20 uniquement</span>
                      </span>
                      <div className="input-container">
                        <input
                          type="text"
                          placeholder="Ex. TXyz..."
                          value={withdrawAddress}
                          onChange={(e) => setWithdrawAddress(e.target.value)}
                          required
                          className="withdraw-input"
                        />
                      </div>
                    </label>
                    <div className="withdraw-address-warning">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="withdraw-warning-icon">
                        <path d="M12 9V14M12 17.5V18M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <div>
                        <strong>Vérifiez soigneusement l'adresse.</strong>
                        <span>Un envoi vers une adresse incorrecte ne peut pas être annulé ou remboursé.</span>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="withdraw-label">
                      <span className="withdraw-field-heading">
                        <span className="withdraw-field-label">Montant à retirer</span>
                        <span className="withdraw-field-helper">Minimum : 10 USDT</span>
                      </span>
                      <div className="input-container withdraw-amount-container">
                        <input
                          type="number"
                          placeholder="0.00"
                          value={withdrawAmount}
                          onChange={(e) => setWithdrawAmount(e.target.value)}
                          min="10"
                          step="any"
                          required
                          className="withdraw-input"
                        />
                        <span className="currency-suffix">USDT</span>
                        <button
                          type="button"
                          className="max-btn"
                          onClick={() => user && setWithdrawAmount(user.balance_usdt)}
                        >
                          Max
                        </button>
                      </div>
                    </label>
                  </div>

                  <div className={`withdraw-receipt-preview ${estimatedReceived ? 'has-value' : ''}`}>
                    <div className="withdraw-receipt-main">
                      <span>Montant estimé à recevoir</span>
                      <strong>{estimatedReceived || '—'} <small>USDT</small></strong>
                    </div>
                    <div className="withdraw-receipt-breakdown">
                      <div>
                        <span>Frais de réseau</span>
                        <strong>1 USDT</strong>
                      </div>
                      <div>
                        <span>Délai estimé</span>
                        <strong>1–60 min</strong>
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="withdraw-btn" disabled={isRequestingEmailCode}>
                    <span>{isRequestingEmailCode ? 'Envoi du code...' : 'Continuer'}</span>
                    {!isRequestingEmailCode && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                  <div className="withdraw-security-note">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 3l7 3v5c0 4.6-2.9 8.8-7 10-4.1-1.2-7-5.4-7-10V6l7-3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Retrait protégé par une vérification email
                  </div>
                </form>
              )}

              {withdrawState === 'email_verification' && (
                <form className="withdraw-form withdraw-email-verification" onSubmit={(e) => { e.preventDefault(); handleWithdraw(true); }}>
                  <div className="withdraw-verification-card">
                    <h3>Vérification email</h3>
                    <p>
                      Nous avons envoyé un code à {user?.email}. Saisissez ce code pour confirmer le retrait avant son traitement.
                    </p>
                    <div className="withdraw-verification-summary">
                      <div className="info-item">
                        <span>Montant</span>
                        <span>{parseFloat(withdrawAmount).toFixed(2)} USDT</span>
                      </div>
                      <div className="info-item">
                        <span>Adresse</span>
                        <span className="withdraw-verification-address">{withdrawAddress}</span>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="withdraw-label">
                      <span className="withdraw-field-heading">
                        <span className="withdraw-field-label">Code reçu par email</span>
                        <span className="withdraw-field-helper">6 chiffres</span>
                      </span>
                      <div className="input-container">
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="Entrer le code à 6 chiffres"
                          value={withdrawEmailCode}
                          onChange={(e) => setWithdrawEmailCode(e.target.value)}
                          required
                          className="withdraw-input"
                          maxLength="6"
                        />
                      </div>
                    </label>
                  </div>

                  <button type="submit" className="withdraw-btn" disabled={isConfirmingWithdraw}>
                    <span>{isConfirmingWithdraw ? 'Confirmation...' : 'Confirmer le retrait'}</span>
                  </button>
                  <div className="withdraw-verification-actions">
                    <button
                      type="button"
                      className="withdraw-link-btn"
                      onClick={requestWithdrawEmailCode}
                      disabled={isRequestingEmailCode}
                    >
                      {isRequestingEmailCode ? 'Renvoi en cours...' : 'Renvoyer le code'}
                    </button>
                    <span className="withdraw-action-separator">•</span>
                    <button
                      type="button"
                      className="withdraw-link-btn"
                      onClick={() => { setWithdrawState('idle'); setWithdrawEmailCode(''); }}
                    >
                      Modifier le retrait
                    </button>
                  </div>
                </form>
              )}

              {withdrawState === 'processing' && (
                <div className="withdraw-processing">
                  <div className="processing-animation">
                    <div className="spinner"></div>
                  </div>
                  <h3>Traitement en cours</h3>
                  <p>Votre demande de retrait est en cours de traitement...</p>
                </div>
              )}

              {(withdrawState === 'success' || withdrawState === 'failed' || withdrawState === 'timeout') && (
                <div className={`withdraw-result ${withdrawState}`}>
                  <div className="result-icon">
                    {withdrawState === 'success' ? (
                      <svg width="64" height="64" fill="none" viewBox="0 0 48 48">
                        <circle cx="24" cy="24" r="24" fill="#34a853"/>
                        <path d="M34 18l-12 12-6-6" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="64" height="64" fill="none" viewBox="0 0 48 48">
                        <circle cx="24" cy="24" r="24" fill="#ea4335"/>
                        <path d="M30 18L18 30M18 18l12 12" stroke="#fff" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                    )}
                  </div>
                  <h3>{withdrawState === 'success' ? 'Retrait effectué' : 'Échec du retrait'}</h3>
                  <p className="result-message">
                    {withdrawState === 'success'
                      ? withdrawResultMessage
                      : 'Une erreur est survenue lors du traitement du retrait. Si le problème persiste, veuillez contacter le support.'}
                  </p>
                  <button
                    onClick={() => { setWithdrawState('idle'); setWithdrawAmount(''); setWithdrawAddress(''); setWithdrawEmailCode(''); }}
                    className="withdraw-btn"
                  >
                    Nouveau retrait
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="withdraw-history">
              <div className="exchange-history-header">
                <h3 className="exchange-history-title">Historique des retraits</h3>
                <button
                  onClick={() => updateHistoryState(false)}
                  className="exchange-history-close"
                  title="Fermer"
                >
                  ×
                </button>
              </div>
              {withdrawHistory === null ? (
                <div className="withdraw-history-loading">
                  Chargement de l'historique...
                </div>
              ) : withdrawHistory.length === 0 ? (
                <div className="withdraw-history-empty">
                  Aucun retrait enregistré.
                </div>
              ) : (
                <div className="exchange-history-table-wrapper">
                  <table className="exchange-history-table-full">
                    <thead>
                      <tr className="exchange-history-table-header-bg">
                        <th className="exchange-history-table-cell-header-new">Date</th>
                        <th className="exchange-history-table-cell-header-new">Montant</th>
                        <th className="exchange-history-table-cell-header-new">Adresse USDT</th>
                        <th className="exchange-history-table-cell-header-new">TRX ID</th>
                        <th className="exchange-history-table-cell-header-new">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {withdrawHistory.map((withdraw) => {
                        const statusInfo = getStatusBadge(withdraw.status);
                        return (
                          <tr key={withdraw.id} className="exchange-history-table-row-new">
                            <td className="exchange-history-table-data-new" data-label="Date">
                              {formatDate(withdraw.created_at)}
                            </td>
                            <td className="exchange-history-table-data-new exchange-history-table-data-bold-new" data-label="Montant">
                              {parseFloat(withdraw.amount).toFixed(2)} USDT
                            </td>
                            <td className="exchange-history-table-data-new withdraw-history-table-data-address" data-label="Adresse USDT">
                              {withdraw.address}
                            </td>
                            <td className="exchange-history-table-data-new withdraw-history-table-data-txid" data-label="TRX ID">
                              {withdraw.tx_id ? (
                                <a 
                                  href={`https://tronscan.org/#/transaction/${withdraw.tx_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="withdraw-tx-link"
                                >
                                  {withdraw.tx_id}
                                </a>
                              ) : (
                                <span className="withdraw-tx-placeholder">
                                  {[1, 3, 5].includes(withdraw.status) ? 'Non disponible' : 'En attente'}
                                </span>
                              )}
                            </td>
                            <td className="exchange-history-table-data-new" data-label="Statut">
                              <span className={`transaction-status ${statusInfo.class} withdraw-status-badge`}>
                                {statusInfo.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </section>
    </>
  );
};

export default WithdrawPage;
